import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PageContainer from "@/components/layouts/PageContainer";
import { Heading } from "@/components/heading";
import { Text } from "@/components/text";
import { Button } from "@/components/button";
import {
  ArrowLeftIcon,
  PencilIcon,
  TrashIcon,
  UsersIcon,
  UserPlusIcon,
} from "@heroicons/react/20/solid";
import useQueryGetGroup from "@/apis/groups/getGroup";
import useDeleteGroup from "@/apis/groups/deleteGroup";
import { useMutationUpdateGroupUsers } from "@/apis/groups/updateGroupUsers";
import { Badge } from "@/components/badge";
import GroupDetails from "@/pages/groups/components/GroupDetails";
import GroupMemberModal from "./components/GroupMemberModal";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/table";

const GroupDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  if (!id) {
    navigate("/groups");
    return null;
  }

  // Query group data
  const { data: group, isLoading, error } = useQueryGetGroup(id);

  // Delete group mutation
  const { mutate: deleteGroup, isPending: isDeleting } = useDeleteGroup();

  // Update group users mutation
  const { mutate: updateGroupUsers, isPending: isUpdating } =
    useMutationUpdateGroupUsers();

  // Go back to list
  const handleBack = () => {
    navigate("/groups");
  };

  // Go to edit page
  const handleEdit = () => {
    navigate(`/groups/${id}/edit`);
  };

  // Handle delete
  const handleDelete = () => {
    if (!group || isDeleting) return;

    if (
      window.confirm(
        `Are you sure you want to delete the group "${group.name}"?`
      )
    ) {
      setDeleteError(null);

      deleteGroup(id, {
        onSuccess: () => {
          // Navigate to the group list
          navigate("/groups");
        },
        onError: (error: any) => {
          // Show error message
          setDeleteError(error?.message || "Failed to delete group");
        },
      });
    }
  };

  // 모달 열기
  const openModal = () => {
    setIsModalOpen(true);
  };

  // 모달 닫기
  const closeModal = () => {
    setIsModalOpen(false);
  };

  // 멤버 업데이트 핸들러
  const handleUpdateMembers = (userIds: string[]) => {
    if (!group) return;

    // API 호출 - 선택된 사용자들로 전체 멤버 목록 업데이트
    updateGroupUsers(
      {
        groupId: id,
        data: { user_ids: userIds },
      },
      {
        onSuccess: () => {
          // 모달 닫기
          setIsModalOpen(false);
        },
        onError: (error: any) => {
          // 오류 처리
          setDeleteError(error?.message || "Failed to update members");
        },
      }
    );
  };

  // Loading state
  if (isLoading) {
    return (
      <PageContainer>
        <div className="p-8 text-center">
          <Text>Loading group information...</Text>
        </div>
      </PageContainer>
    );
  }

  // Error state
  if (error || !group) {
    return (
      <PageContainer>
        <div className="p-8 text-center text-red-600">
          <Text>Failed to load group information. Please try again.</Text>
          <Button onClick={handleBack} className="mt-4">
            Back to List
          </Button>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="space-y-6">
        {deleteError && (
          <div className="p-4 bg-red-50 text-red-600 rounded-md">
            {deleteError}
          </div>
        )}

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button plain onClick={handleBack}>
              <ArrowLeftIcon className="h-5 w-5" />
              Back
            </Button>

            <div>
              <div className="flex items-center gap-2">
                <Heading>Group: {group.name}</Heading>
                {group.status === "active" ? (
                  <Badge color="lime">Active</Badge>
                ) : (
                  <Badge color="zinc">Inactive</Badge>
                )}
              </div>
              <Text className="text-zinc-500">
                View and manage group information and members.
              </Text>
            </div>
          </div>

          <div className="flex gap-2">
            <Button onClick={handleEdit}>
              <PencilIcon className="h-4 w-4" />
              Edit
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              <TrashIcon className="h-4 w-4" />
              {isDeleting ? "Deleting..." : "Delete"}
            </Button>
          </div>
        </div>

        {/* Group Details Component */}
        <div className="p-8 border border-gray-200 rounded-md bg-white">
          <GroupDetails group={group} />
        </div>

        {/* Group Members */}
        <div className="p-8 border border-gray-200 rounded-md bg-white">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <UsersIcon className="h-5 w-5 text-zinc-500" />
              <Heading level={3}>Group Members</Heading>
            </div>
            <Button onClick={openModal}>
              <UserPlusIcon className="h-4 w-4 mr-1" />
              Manage Members
            </Button>
          </div>

          {!group.users || group.users.length === 0 ? (
            <Text className="text-center text-zinc-500 py-4">
              No members in this group yet.
            </Text>
          ) : (
            <>
              <Text className="text-zinc-600 mb-4">
                This group has {group.users.length} members.
              </Text>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeader>Name</TableHeader>
                    <TableHeader>Email</TableHeader>
                    <TableHeader>School</TableHeader>
                    <TableHeader>Grade</TableHeader>
                    <TableHeader>Status</TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {group.users.map((user) => (
                    <TableRow
                      key={user.id}
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => navigate(`/users/${user.id}`)}
                    >
                      <TableCell className="font-medium">{user.name}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{user.school ?? "-"}</TableCell>
                      <TableCell>{user.grade || "-"}</TableCell>
                      <TableCell>
                        {user.status === "active" ? (
                          <Badge color="lime">Active</Badge>
                        ) : (
                          <Badge color="zinc">Inactive</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </div>

        {/* 멤버 추가 모달 */}
        <GroupMemberModal
          open={isModalOpen}
          onClose={closeModal}
          onUpdateMembers={handleUpdateMembers}
          currentMembers={group.users?.map((user) => user.id) || []}
          currentGroupId={group.id}
        />
      </div>
    </PageContainer>
  );
};

export default GroupDetailPage;
